import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useTenant } from '../../contexts/TenantContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { notificationsAPI } from '../../lib/api';
import { LanguageSelector } from '../LanguageSelector';
import {
  LayoutDashboard,
  BookOpen,
  Route,
  Trophy,
  Award,
  MessageCircle,
  Heart,
  ShieldCheck,
  BarChart3,
  Users,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Bell,
  Check,
  PanelLeftClose,
  PanelLeft,
  Network,
  Film,
  HelpCircle,
  X,
} from 'lucide-react';

const DEFAULT_LOGO = 'https://static.prod-images.emergentagent.com/jobs/2d9de157-6aba-4791-b4d7-cfe9f78303cb/images/caa72923253f39d305789ea60b60a4f20b404a1e2c8d1c106a92bfb7211cd8c8.png';

// Navigation items with translation keys (Discussions shown only to admins via filter)
const getNavItems = (t) => [
  { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
  { path: '/courses', icon: BookOpen, label: t('nav.courses') },
  { path: '/learning-paths', icon: Route, label: t('nav.learningPaths') },
  { path: '/leaderboard', icon: Trophy, label: t('nav.leaderboard') },
  { path: '/achievements', icon: Award, label: t('nav.achievements') },
  { path: '/discussions', icon: MessageCircle, label: t('nav.discussions'), adminOnly: true },
  { path: '/recognition', icon: Heart, label: t('nav.recognition') },
  { path: '/compliance', icon: ShieldCheck, label: t('nav.compliance') },
];

const getManagerNavItems = (t) => [
  { path: '/manager', icon: BarChart3, label: t('nav.managerDashboard') },
  { path: '/my-team', icon: Users, label: t('nav.myTeam', 'My Team') },
];

// Admin nav items - AI Videos requires AI features
const getAdminNavItems = (t, hasAIFeatures = true) => {
  const items = [
    { path: '/course-management', icon: BookOpen, label: t('nav.courseManagement') },
  ];
  
  // Only show AI Videos if AI features are enabled
  if (hasAIFeatures) {
    items.push({ path: '/ai-videos', icon: Film, label: t('nav.aiVideos', 'AI Videos') });
  }
  
  items.push(
    { path: '/analytics', icon: BarChart3, label: t('nav.analytics') },
    { path: '/admin', icon: Users, label: t('nav.userManagement') },
    { path: '/daily-questions', icon: HelpCircle, label: t('nav.dailyQuestions', 'Daily Questions') },
    { path: '/org-structure', icon: Network, label: t('nav.orgStructure', 'Org Structure') },
    { path: '/settings', icon: Settings, label: t('nav.adminSettings') },
  );
  
  return items;
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const { hasAnyAIFeature, isPlatformUser } = useTenant();
  const { t } = useTranslation();
  
  // Get translated nav items - AI Videos only for users with AI features
  const navItems = getNavItems(t);
  const managerNavItems = getManagerNavItems(t);
  const hasAI = isPlatformUser || hasAnyAIFeature();
  const adminNavItems = getAdminNavItems(t, hasAI);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  // Load sidebar collapsed state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      setSidebarCollapsed(JSON.parse(saved));
    }
  }, []);

  // Preserve and restore sidebar scroll position
  useEffect(() => {
    // Restore scroll position on mount and route change
    const scrollContainer = document.querySelector('[data-sidebar-scroll]');
    if (scrollContainer) {
      const savedScrollPos = sessionStorage.getItem('sidebarScrollPosition');
      if (savedScrollPos) {
        scrollContainer.scrollTop = parseInt(savedScrollPos, 10);
      }
    }

    // Save scroll position when it changes
    const handleScroll = (e) => {
      sessionStorage.setItem('sidebarScrollPosition', e.target.scrollTop.toString());
    };

    const scrollEl = document.querySelector('[data-sidebar-scroll]');
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll);
      return () => scrollEl.removeEventListener('scroll', handleScroll);
    }
  }, [location.pathname]);

  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  // Logo zoom modal state
  const [logoZoomOpen, setLogoZoomOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        notificationsAPI.getAll(),
        notificationsAPI.getUnreadCount(),
      ]);
      // Handle both response formats: {notifications: [...]} or direct array
      const notifs = notifsRes.data.notifications || notifsRes.data || [];
      setNotifications(Array.isArray(notifs) ? notifs.slice(0, 10) : []);
      // Handle both response formats: {count, unread_count}
      setUnreadCount(countRes.data.count || countRes.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getAllNavItems = () => {
    // Filter base items: adminOnly entries visible only to admins
    let items = navItems.filter(item => !item.adminOnly || user?.role === 'admin');
    if (user?.role === 'store_manager' || user?.role === 'regional_manager' || user?.role === 'admin') {
      items = [...items, ...managerNavItems];
    }
    if (user?.role === 'admin' || user?.role === 'training_team') {
      items = [...items, ...adminNavItems];
    }
    return items;
  };

  // Ref to preserve scroll position
  const scrollPositionRef = React.useRef(0);

  // Save scroll position before navigation
  const handleNavClick = (e) => {
    // Save the current scroll position from the sidebar
    const sidebarScroll = document.querySelector('[data-sidebar-scroll]');
    if (sidebarScroll) {
      scrollPositionRef.current = sidebarScroll.scrollTop;
    }
    setSidebarOpen(false);
  };

  // Restore scroll position after navigation
  React.useEffect(() => {
    // Small delay to ensure the DOM is ready after navigation
    const timer = setTimeout(() => {
      const sidebarScroll = document.querySelector('[data-sidebar-scroll]');
      if (sidebarScroll && scrollPositionRef.current > 0) {
        sidebarScroll.scrollTop = scrollPositionRef.current;
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Render navigation items
  const renderNavItems = (collapsed = false, scrollRef = null) => (
    <nav className="flex flex-col gap-0.5 p-1.5" ref={scrollRef}>
      {getAllNavItems().map((item) => {
        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            title={collapsed ? item.label : undefined}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 group ${
              collapsed ? 'justify-center' : ''
            } ${
              isActive
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <item.icon className={`w-4.5 h-4.5 flex-shrink-0 ${isActive ? '' : 'group-hover:scale-110 transition-transform'}`} />
            {!collapsed && <span className="font-medium text-sm truncate">{item.label}</span>}
            {!collapsed && isActive && <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-0 z-40 hidden lg:flex h-screen flex-col border-r bg-card transition-all duration-300 ${
        sidebarCollapsed ? 'w-20' : 'w-64'
      }`}>
        {/* Logo */}
        <div 
          className={`p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors ${sidebarCollapsed ? 'flex justify-center' : ''}`}
          onClick={() => setLogoZoomOpen(true)}
          title="Click to view logo"
        >
          {sidebarCollapsed ? (
            <img 
              src={branding.logo_url || DEFAULT_LOGO} 
              alt={branding.app_name} 
              className="h-12 w-auto"
            />
          ) : (
            <div className="flex items-center gap-3 px-1">
              <img 
                src={branding.logo_url || DEFAULT_LOGO} 
                alt={branding.app_name} 
                className="h-14 w-14 object-contain flex-shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <h1 className="text-lg font-bold text-foreground tracking-wide">{branding.app_name || 'Gyanodaya'}</h1>
                <p className="text-xs text-muted-foreground">{branding.slogan || 'Rising of Knowledge'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation - preserve scroll position */}
        <div 
          data-sidebar-scroll
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"
        >
          {renderNavItems(sidebarCollapsed)}
        </div>

        {/* Collapse Toggle Button */}
        <div className="p-3 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebarCollapse}
            className={`w-full flex items-center gap-2 ${sidebarCollapsed ? 'justify-center' : ''}`}
            data-testid="sidebar-collapse-btn"
          >
            {sidebarCollapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5" />
                <span>Collapse</span>
              </>
            )}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 h-16 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="flex items-center gap-3">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col">
              <div 
                className="p-3 border-b flex-shrink-0 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setLogoZoomOpen(true)}
              >
                <div className="flex items-center gap-3 px-1">
                  <img 
                    src={branding.logo_url || DEFAULT_LOGO} 
                    alt={branding.app_name} 
                    className="h-12 w-12 object-contain flex-shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <h1 className="text-lg font-bold tracking-wide">{branding.app_name || 'Gyanodaya'}</h1>
                    <p className="text-xs text-muted-foreground">{branding.slogan || 'Rising of Knowledge'}</p>
                  </div>
                </div>
              </div>
              <ScrollArea className="flex-1">
                {renderNavItems()}
              </ScrollArea>
            </SheetContent>
          </Sheet>
          <img src={branding.logo_url || DEFAULT_LOGO} alt={branding.app_name} className="h-8 w-auto" />
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/10">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-amber-500">{user?.points || 0}</span>
          </div>
          
          {/* Notification Bell - Mobile */}
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-mobile">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="font-semibold">Notifications</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                    <Check className="w-4 h-4 mr-1" /> Mark all read
                  </Button>
                )}
              </div>
              <ScrollArea className="h-72">
                {notifications.length > 0 ? (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 border-b last:border-0 ${!notif.read ? 'bg-primary/5' : ''}`}
                    >
                      <p className="font-medium text-sm">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-mobile">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-mobile">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Header */}
      <header className={`hidden lg:flex fixed top-0 right-0 z-40 items-center justify-between px-6 h-16 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 transition-all duration-300 ${
        sidebarCollapsed ? 'left-20' : 'left-64'
      }`}>
        <div>
          <h2 className="text-lg font-semibold">
            {(() => {
              const hour = new Date().getHours();
              let greeting = t('dashboard.goodMorning', 'Good Morning');
              if (hour >= 12 && hour < 17) greeting = t('dashboard.goodAfternoon', 'Good Afternoon');
              else if (hour >= 17 && hour < 21) greeting = t('dashboard.goodEvening', 'Good Evening');
              else if (hour >= 21 || hour < 5) greeting = t('dashboard.goodNight', 'Good Night');
              return greeting;
            })()}, {user?.name?.split(' ')[0] || 'Learner'}
          </h2>
          <p className="text-sm text-muted-foreground">{t('dashboard.continueYourJourney', 'Continue your learning journey')}</p>
        </div>

        <div className="flex items-center gap-4">
          {/* Notification Bell - Desktop */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-desktop">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="font-semibold">{t('notifications.title', 'Notifications')}</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                    <Check className="w-4 h-4 mr-1" /> {t('notifications.markAllRead', 'Mark all read')}
                  </Button>
                )}
              </div>
              <ScrollArea className="h-72">
                {notifications.length > 0 ? (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 border-b last:border-0 ${!notif.read ? 'bg-primary/5' : ''}`}
                    >
                      <p className="font-medium text-sm">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-desktop">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Language Selector */}
          <LanguageSelector showLabel={false} />

          {/* Points Badge */}
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/10">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold text-amber-500">{user?.points || 0} pts</span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-3 h-auto py-2" data-testid="user-menu-desktop">
                <Avatar className="h-9 w-9 border-2 border-primary/20">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left hidden xl:block">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Settings & Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className={`pt-16 min-h-screen pb-24 lg:pb-8 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
        {/* Footer */}
        <footer className="hidden lg:block text-center py-4 text-sm text-muted-foreground border-t mt-8">
          © 2025 Copyright, Design and Developed by <a href="https://www.scatterpie.io" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">ScatterPie Analytics</a>
        </footer>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav 
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around px-2 h-16 border-t bg-card"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {navItems.filter(item => !item.adminOnly || user?.role === 'admin').slice(0, 5).map((item) => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg transition-colors ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <item.icon className={`w-5 h-5 ${isActive ? 'scale-110' : ''} transition-transform`} />
              <span className="text-xs font-medium">{item.label.split(' ')[0]}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logo Zoom Dialog */}
      <Dialog open={logoZoomOpen} onOpenChange={setLogoZoomOpen}>
        <DialogContent className="max-w-md p-0 bg-transparent border-0 shadow-none">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-10 right-0 text-white hover:bg-white/20 rounded-full"
              onClick={() => setLogoZoomOpen(false)}
            >
              <X className="w-6 h-6" />
            </Button>
            <div className="bg-card rounded-xl p-6 shadow-2xl text-center">
              <img 
                src={branding.logo_url || DEFAULT_LOGO} 
                alt={branding.app_name} 
                className="w-40 h-auto mx-auto mb-4"
              />
              <h2 className="text-2xl font-bold tracking-wide">{branding.app_name || 'Gyanodaya'}</h2>
              <p className="text-muted-foreground text-sm mt-1">{branding.slogan || 'Rising of Knowledge'}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardLayout;
